import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse } from '../../types';
import { parseQueryParams } from '../../utils';
import { baseQueryWithReauth } from './baseQuery';
import { IBulkApprovalActionDto } from '../../pages/TravelRequest/constants/travelreq.interface';
import { IContractListQuery } from '../../pages/ContractManagement/core/constants/contract.interface';
import { LogsSortByOptions } from '../../pages/TravelRequest/TravelReqDetail';
import { ExportActions } from '../../enums';
import { encryptPassword } from '../../utils/encryption';
import type {
  Vendor,
  ContractVendor,
  CreateVendorRequest,
  UpdateVendorRequest,
  VendorSearchParams,
} from '../../pages/ContractManagement/core/contracts/types/contract-vendor-types';
import type {
  ContractSummary,
  ApprovalFunnel,
  PendingApprovalRequests,
  CreationTrendsResponse,
  CreationTrendsParams,
  ApprovalTime,
  ApprovalTimeParams,
  TopVendorsResponse,
  TopVendorsParams,
  SecurityAmountTracking,
  SecurityAmountParams,
  ContractVendorPortfolio,
  VendorPortfolioParams,
  ContractHomeDashboard,
  ExpiringContractsResponse,
  ExpiringContractsParams,
  DurationDistribution,
  DurationDistributionParams,
} from '../../pages/ContractManagement/core/contracts/types/contract-analytics-types';
interface Collaborator {
  name: string;
  email: string;
  documentUrl: string;
}

const contractDocumentDraftApi = createApi({
  reducerPath: 'contractDocumentDraft',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'ContractDocumentDrafts',
    'ContractDocumentFields',
    'ContractDocumentTemplates',
    'ContractTemplateTypes',
    'Contracts',
    'ContractHistoryList',
    'ContractDocumentHistory',
    'SapConfigurationFieldSet',
    'ContractAddresses',
    'ContractApprovals',
    'ContractReports',
    'ContractApprovalMapping',
    'ClaimAttachmentJson',
    'FormFieldMapping',
    'UniversalFieldSet',
    'ResetApprovalHierarchy',
    'RearrangeApprovalHierarchy',
  ],
  endpoints: builder => ({
    getContractHomeDashboard: builder.query<
      BackendResponse<ContractHomeDashboard>,
      Partial<{
        startDate: string;
        endDate: string;
        companyGroupId: number;
      }>
    >({
      query: params => ({
        url: `/contract-analytics/home${parseQueryParams(params)}`,
        method: 'GET',
      }),
    }),
    getContractTypesDropdown: builder.query<
      BackendResponse<
        {
          id: number;
          name: string;
          description?: string;
        }[]
      >,
      void
    >({
      query: () => ({
        url: `/contract/contract-types-dropdown`,
        method: 'GET',
      }),
      providesTags: (__, error) => {
        if (error) {
          return [];
        }
        return ['ContractTemplateTypes'];
      },
    }),
    getContractDocumentTemplates: builder.query<
      BackendResponse<{ count: number; list: any[] }>,
      Partial<{
        page: number;
        pageSize: number;
        all: boolean;
        search: string;
      }>
    >({
      query: params => ({
        url: `/contract-document-templates${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['ContractDocumentTemplates'],
    }),
    getContractDocumentTemplateById: builder.query<
      BackendResponse<any>,
      number
    >({
      query: id => ({
        url: `/contract-document-templates/${id}`,
        method: 'GET',
      }),
      providesTags: (_, error, id) =>
        error ? [] : [{ type: 'ContractDocumentTemplates', id }],
    }),
    createContractDocumentTemplate: builder.mutation<
      BackendResponse<any>,
      {
        name: string;
        slug?: string;
        description?: string;
        docContent: any;
      }
    >({
      query: payload => ({
        url: `/contract-document-templates`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) =>
        error ? [] : ['ContractDocumentTemplates'],
    }),
    updateContractDocumentTemplate: builder.mutation<
      BackendResponse<any>,
      {
        id: number;
        name?: string;
        slug?: string;
        description?: string;
        docContent?: any;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/contract-document-templates/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_, error, { id }) =>
        error
          ? []
          : [
              'ContractDocumentTemplates',
              { type: 'ContractDocumentTemplates', id },
            ],
    }),
    deleteContractDocumentTemplate: builder.mutation<
      BackendResponse<any>,
      number
    >({
      query: id => ({
        url: `/contract-document-templates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error) =>
        error ? [] : ['ContractDocumentTemplates'],
    }),
    getContractDocumentTemplateVariables: builder.query<
      BackendResponse<any[]>,
      Partial<{ search: string }> | void
    >({
      query: params => ({
        url: `/contract-document-templates/variables${parseQueryParams(
          params || {},
        )}`,
        method: 'GET',
      }),
      providesTags: ['UniversalFieldSet'],
    }),
    getInvoiceFrequencyOptions: builder.query<
      BackendResponse<{ id: number; name: string; description?: string }[]>,
      void
    >({
      query: () => ({
        url: `/contract/invoice-frequency-options`,
        method: 'GET',
      }),
      providesTags: ['Contracts'],
    }),

    getUnitTypes: builder.query<BackendResponse<any>, void>({
      query: () => ({
        url: `/master/unit-types`,
        method: 'GET',
      }),
      providesTags: ['Contracts'],
    }),

    getOperationalUnits: builder.query<BackendResponse<any>, void>({
      query: () => ({
        url: `/master/operational-unit`,
        method: 'GET',
      }),
      providesTags: ['Contracts'],
    }),

    getContracts: builder.query<BackendResponse<any>, any>({
      query: query => ({
        url: `/contract${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: (__, error) => {
        if (error) {
          return [];
        }
        return ['Contracts'];
      },
    }),
    createContractDraft: builder.mutation<
      BackendResponse<any>,
      { contractType: number; contractTypeId?: number; formData: any }
    >({
      query: ({ contractType, contractTypeId, formData }) => ({
        url: `/contract/create-draft`,
        method: 'POST',
        body: {
          contractType,
          ...(contractTypeId && { contractTypeId }),
          ...formData,
        },
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['Contracts'];
      },
    }),
    createContractWithReference: builder.mutation<BackendResponse<any>, number>(
      {
        query: sourceContractId => ({
          url: `/contract/create-with-reference/${sourceContractId}`,
          method: 'POST',
        }),
        invalidatesTags: (_, error) => {
          if (error) {
            return [];
          }
          return ['Contracts'];
        },
      },
    ),
    createRenewalContract: builder.mutation<BackendResponse<any>, number>({
      query: contractId => ({
        url: `/contract/create-renewal-contract/${contractId}`,
        method: 'POST',
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['Contracts'];
      },
    }),
    getContractRequestSubcontracts: builder.query<BackendResponse<any>, string>(
      {
        query: requestNo => ({
          url: `/contract/request/${requestNo}/subcontracts`,
          method: 'GET',
        }),
        providesTags: (__, error) => {
          if (error) {
            return [];
          }
          return ['Contracts'];
        },
      },
    ),
    updateContractDraft: builder.mutation<
      BackendResponse<any>,
      { id: number; formData: any }
    >({
      query: ({ id, formData }) => ({
        url: `/contract/edit-draft/${id}`,
        method: 'PATCH',
        body: formData,
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['Contracts'];
      },
    }),

    getContractDraftById: builder.query<BackendResponse<any>, number>({
      query: id => ({
        url: `/contract/${id}`,
        method: 'GET',
      }),
      providesTags: (__, error, id) => {
        if (error) {
          return [];
        }
        return [{ type: 'ContractDocumentDrafts', id }];
      },
    }),

    getContractDocument: builder.query<
      BackendResponse<{
        contractId: number;
        docName: string;
        docContent: Record<string, unknown>;
        version: number;
        docCreatedAt: string | null;
        createdBy: string | null;
      }>,
      number
    >({
      query: id => ({
        url: `/contract/${id}/document`,
        method: 'GET',
      }),
      providesTags: (__, error, id) => {
        if (error) {
          return [];
        }
        return [
          { type: 'ContractDocumentDrafts', id },
          { type: 'ContractDocumentDrafts', id: `${id}-document` },
        ];
      },
    }),

    updateContractDocument: builder.mutation<
      BackendResponse<any>,
      {
        id: number;
        docContent: Record<string, unknown>;
        docName: string;
        mappings?: any[];
        documentVariableValues: Record<string, any>;
      }
    >({
      query: ({
        id,
        docContent,
        docName,
        mappings,
        documentVariableValues,
      }) => ({
        url: `/contract/${id}/document`,
        method: 'PUT',
        body: {
          ...(docContent ? { docContent } : {}),
          ...(docName?.trim() ? { docName: docName.trim() } : {}),
          ...(mappings?.length ? { mappings } : {}),
          ...(documentVariableValues &&
          Object.keys(documentVariableValues).length
            ? { documentVariableValues }
            : {}),
        },
      }),
      invalidatesTags: (_, error, { id }) => {
        if (error) {
          return [];
        }
        return [
          { type: 'ContractDocumentDrafts', id },
          { type: 'ContractDocumentDrafts', id: `${id}-document` },
          'Contracts',
        ];
      },
    }),

    updateContractTokenDocument: builder.mutation<
      BackendResponse<{
        contractId: number;
      }>,
      {
        id: number;
        accessToken: string | null;
        editorId: number | null;
        collaborators?: Collaborator[];
      }
    >({
      query: ({ id, accessToken, editorId, collaborators }) => ({
        url: `/contract/${id}/document-token`,
        method: 'PUT',
        body: {
          accessToken,
          editorId,
          ...(collaborators?.length ? { collaborators } : {}),
        },
      }),
      invalidatesTags: (_, error, { id }) => {
        if (error) {
          return [];
        }

        return [
          { type: 'ContractDocumentDrafts', id },
          { type: 'ContractDocumentDrafts', id: `${id}-document` },
          'Contracts',
        ];
      },
    }),

    getRecentContractDrafts: builder.query<BackendResponse<any>, void>({
      query: () => ({
        url: `/contract/recent-drafts`,
        method: 'GET',
      }),
      providesTags: ['ContractDocumentDrafts'],
    }),

    uploadContractAttachment: builder.mutation<
      BackendResponse<any>,
      {
        contractId: number;
        file: File;
        remarks?: string;
        documentType: string;
      }
    >({
      query: ({ contractId, file, remarks, documentType }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('documentType', documentType);
        if (remarks) {
          formData.append('remarks', remarks);
        }

        return {
          url: `/contract-attachments/${contractId}/upload`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (_, error, { contractId }) => {
        if (error) {
          return [];
        }
        return ['Contracts', { type: 'Contracts', id: contractId }];
      },
    }),

    getContractAttachments: builder.query<BackendResponse<any>, number>({
      query: contractId => ({
        url: `/contract-attachments/${contractId}/all`,
        method: 'GET',
      }),
      providesTags: (__, error, contractId) => {
        if (error) {
          return [];
        }
        return ['Contracts', { type: 'Contracts', id: contractId }];
      },
    }),
    getContractAttachmentFile: builder.query<
      Blob,
      { contractId: number; attachmentId: number }
    >({
      query: ({ contractId, attachmentId }) => ({
        url: `/contract-attachments/${contractId}/file/${attachmentId}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
      providesTags: (__, error, { contractId, attachmentId }) => {
        if (error) {
          return [];
        }
        return [
          'Contracts',
          { type: 'Contracts', id: contractId },
          {
            type: 'ContractDocumentDrafts',
            id: `${contractId}-${attachmentId}`,
          },
        ];
      },
    }),
    deleteContractAttachment: builder.mutation<
      BackendResponse<void>,
      { contractId: number; attachmentId: number }
    >({
      query: ({ attachmentId }) => ({
        url: `/contract-attachments/${attachmentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error, { contractId }) => {
        if (error) {
          return [];
        }
        return ['Contracts', { type: 'Contracts', id: contractId }];
      },
    }),
    // getContractApprovalHierarchy: builder.query<BackendResponse<any>, void>({
    //   query: () => ({
    //     url: `/contract-mappings/approval-hierarchy`,
    //     method: 'GET',
    //   }),
    //   providesTags: (_, error) => {
    //     if (error) {
    //       return [];
    //     }
    //     return ['Contracts'];
    //   },
    // }),
    sendContractForApproval: builder.mutation<
      BackendResponse<any>,
      { contractId: number }
    >({
      query: ({ contractId }) => ({
        url: `/contract/${contractId}`,
        method: 'PATCH',
      }),
      invalidatesTags: (_, error, { contractId }) => {
        if (error) {
          return [];
        }
        return ['Contracts', { type: 'Contracts', id: contractId }];
      },
    }),
    getContractConfig: builder.query<BackendResponse<any[]>, number>({
      query: contractId => ({
        url: `/contract/config/${contractId}`,
        method: 'GET',
      }),
      providesTags: (_result, error, contractId) => {
        if (error) {
          return [];
        }
        return [{ type: 'Contracts', id: contractId }];
      },
    }),
    getContractAddresses: builder.query<BackendResponse<any[]>, number>({
      query: contractId => ({
        url: `/contract-address/${contractId}`,
        method: 'GET',
      }),
      providesTags: (_result, error, contractId) => {
        if (error) {
          return [];
        }
        return [{ type: 'ContractAddresses', id: contractId }];
      },
    }),

    createContractAddress: builder.mutation<
      BackendResponse<any>,
      {
        contractId: number;
        addressData: any;
      }
    >({
      query: ({ contractId, addressData }) => ({
        url: `/contract-address/${contractId}/address`,
        method: 'POST',
        body: addressData,
      }),
      invalidatesTags: (_result, error, { contractId }) => {
        if (error) {
          return [];
        }
        return [{ type: 'ContractAddresses', id: contractId }];
      },
    }),

    updateContractAddress: builder.mutation<
      BackendResponse<any>,
      {
        contractId: number;
        addressId: number;
        addressData: any;
      }
    >({
      query: ({ contractId, addressId, addressData }) => ({
        url: `/contract-address/${contractId}/address/${addressId}`,
        method: 'PATCH',
        body: addressData,
      }),
      invalidatesTags: (_result, error, { contractId }) => {
        if (error) {
          return [];
        }
        return [{ type: 'ContractAddresses', id: contractId }];
      },
    }),

    // Vendor Management APIs
    getInvitationTypes: builder.query<BackendResponse<any>, void>({
      query: () => ({
        url: `/vendor-management/invitation-type`,
        method: 'GET',
      }),
      providesTags: ['Contracts'],
    }),

    getBusinessPartnerTypes: builder.query<BackendResponse<any>, void>({
      query: () => ({
        url: `/vendor-management/business-partner-type`,
        method: 'GET',
      }),
      providesTags: ['Contracts'],
    }),

    getBusinessCategories: builder.query<BackendResponse<any>, void>({
      query: () => ({
        url: `/vendor-management/business-partner-category`,
        method: 'GET',
      }),
      providesTags: ['Contracts'],
    }),

    vendorCheck: builder.query<
      BackendResponse<any>,
      { panNo?: string; emailId?: string; mobileNo?: string }
    >({
      query: ({ panNo, emailId, mobileNo }) => ({
        url: `/vendor-management/vendor-check`,
        method: 'GET',
        params: {
          ...(panNo ? { panNo: encryptPassword(panNo) } : {}),
          ...(emailId ? { emailId: encryptPassword(emailId) } : {}),
          ...(mobileNo ? { mobileNo: encryptPassword(mobileNo) } : {}),
        },
      }),
      providesTags: ['Contracts'],
    }),

    // Contract-Vendor Management APIs (Many-to-Many)
    searchVendors: builder.query<BackendResponse<any>, VendorSearchParams>({
      query: ({ search, limit = 50, page, pageSize }) => ({
        url: `/contract/vendors/search`,
        method: 'GET',
        params: {
          ...(search && { search }),
          // Backward compatible: keep `limit` but also support page/pageSize
          ...(limit != null ? { limit } : {}),
          ...(page != null ? { page } : {}),
          ...(pageSize != null ? { pageSize } : {}),
        },
      }),
      providesTags: ['Contracts'],
    }),

    associateVendorToContract: builder.mutation<
      BackendResponse<ContractVendor>,
      { contractId: number; vendorId: number }
    >({
      query: ({ contractId, vendorId }) => ({
        url: `/contract/${contractId}/vendor/${vendorId}`,
        method: 'POST',
      }),
      invalidatesTags: (_, error, { contractId }) => {
        if (error) {
          return [];
        }
        return ['Contracts', { type: 'Contracts', id: contractId }];
      },
    }),

    removeVendorFromContract: builder.mutation<
      BackendResponse<void>,
      { contractId: number; vendorId: number }
    >({
      query: ({ contractId, vendorId }) => ({
        url: `/contract/${contractId}/vendor/${vendorId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error, { contractId }) => {
        if (error) {
          return [];
        }
        return ['Contracts', { type: 'Contracts', id: contractId }];
      },
    }),

    createVendorAndAssociateToContract: builder.mutation<
      BackendResponse<Vendor>,
      { contractId: number; vendorData: CreateVendorRequest }
    >({
      query: ({ contractId, vendorData }) => ({
        url: `/contract/${contractId}/vendor`,
        method: 'POST',
        body: vendorData,
      }),
      invalidatesTags: (_, error, { contractId }) => {
        if (error) {
          return [];
        }
        return ['Contracts', { type: 'Contracts', id: contractId }];
      },
    }),

    updateVendorThroughContract: builder.mutation<
      BackendResponse<Vendor>,
      { contractId: number; vendorId: number; vendorData: UpdateVendorRequest }
    >({
      query: ({ contractId, vendorId, vendorData }) => ({
        url: `/contract/${contractId}/vendor/${vendorId}`,
        method: 'PUT',
        body: vendorData,
      }),
      invalidatesTags: (_, error, { contractId }) => {
        if (error) {
          return [];
        }
        return ['Contracts', { type: 'Contracts', id: contractId }];
      },
    }),

    resendVendorToVConnect: builder.mutation<
      BackendResponse<void>,
      { vendorId: number }
    >({
      query: ({ vendorId }) => ({
        url: `/vendor-management/resend-to-vconnect/${vendorId}`,
        method: 'POST',
      }),
      invalidatesTags: ['Contracts'],
    }),

    updateVendorSharePercentage: builder.mutation<
      BackendResponse<void>,
      {
        contractId: number;
        vendorId: number;
        sharePercentage: number;
        securityAmount?: number | null;
      }
    >({
      query: ({ contractId, vendorId, sharePercentage, securityAmount }) => ({
        url: `/contract/${contractId}/vendor/${vendorId}/share`,
        method: 'PATCH',
        body: { sharePercentage, securityAmount },
      }),
      invalidatesTags: (_, error, { contractId }) => {
        if (error) {
          return [];
        }
        return ['Contracts', { type: 'Contracts', id: contractId }];
      },
    }),

    getContractApprovals: builder.query<
      BackendResponse<any>,
      Partial<IContractListQuery>
    >({
      query: params => ({
        url: `/contract-approvals${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['ContractApprovals'],
    }),

    updateContractApproval: builder.mutation<BackendResponse<any>, any>({
      query: ({ id, ...rest }) => ({
        url: `/contract-approvals/${id}`,
        method: 'POST',
        body: { ...rest },
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['ContractApprovals'];
      },
    }),

    getContractReport: builder.query<
      BackendResponse<{
        list: any[];
        count?: number;
      }>,
      Partial<IContractListQuery>
    >({
      query: params => {
        return {
          url: `/contract-reports${parseQueryParams(params)}`,
          method: 'GET',
        };
      },
      providesTags: (__, err) => (err ? [] : ['ContractReports']),
    }),

    downloadContractUploadSample: builder.query<Blob, void>({
      query: () => ({
        url: '/contract-upload/sample',
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    uploadContractWorkbook: builder.mutation<BackendResponse<any>, FormData>({
      query: formData => ({
        url: '/contract-upload/upload',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['ContractReports']),
    }),

    bulkApproveContracts: builder.mutation<
      BackendResponse<void>,
      IBulkApprovalActionDto
    >({
      query: payload => ({
        url: '/contract-approvals/bulk-approval-action',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['ContractApprovals'],
    }),

    getContractApprovalLogsById: builder.query<
      BackendResponse<any>,
      { id: string; pageSize: number; page: number; sortBy: LogsSortByOptions }
    >({
      query: ({ id, pageSize, page, sortBy }) => ({
        url: `/contract-approvals/logs/${id}`,
        method: 'GET',
        params: { page, pageSize, sortBy },
      }),
      providesTags: (__, err, { id }) =>
        err ? [] : [{ type: 'Contracts', id }],
    }),
    getContractChangeLogsById: builder.query<
      BackendResponse<any>,
      { id: string; pageSize: number; page: number; sortBy: LogsSortByOptions }
    >({
      query: ({ id, pageSize, page, sortBy }) => ({
        url: `/contract/change-logs/${id}`,
        method: 'GET',
        params: { page, pageSize, sortBy },
      }),
      providesTags: (__, err, { id }) =>
        err ? [] : [{ type: 'Contracts', id }],
    }),

    downloadClaimJSONById: builder.mutation<
      BackendResponse<any[]>,
      { id: number }
    >({
      query: ({ id }) => {
        return {
          url: `/contract/download/json`,
          method: 'POST',
          body: {
            id: +id,
          },
        };
      },
      invalidatesTags: (_, error, { id }) =>
        error
          ? []
          : [
              'ContractReports',
              { type: 'Contracts', id },
              { type: 'ContractApprovals' },
              { type: 'ContractReports' },
            ],
    }),
    downloadContractAttachmentJsonById: builder.query<
      BackendResponse<any[]>,
      { id: number }
    >({
      query: ({ id }) => {
        return {
          url: `/contract/download-attachment-json/${id}`,
          method: 'GET',
          responseType: 'blob',
        };
      },
      providesTags: (__, err, { id }) =>
        err ? [] : [{ type: 'ClaimAttachmentJson', id }, 'ClaimAttachmentJson'],
    }),

    printContract: builder.query<Blob, { id: number }>({
      query: ({ id }) => ({
        url: `/contract/print/${id}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
      providesTags: (_, __, { id }) => [{ type: 'Contracts', id }],
    }),

    pushContractToErpById: builder.mutation<
      BackendResponse<any[]>,
      { id: number }
    >({
      query: ({ id }) => {
        return {
          url: `/contract/push/erp`,
          method: 'POST',
          body: {
            id: +id,
          },
        };
      },
      invalidatesTags: (_, error, { id }) =>
        error
          ? []
          : [
              'ContractReports',
              { type: 'Contracts', id },
              { type: 'ContractApprovals' },
              { type: 'ContractReports' },
            ],
    }),

    deleteContract: builder.mutation<BackendResponse<void>, { id: number }>({
      query: ({ id }) => ({
        url: `/contract/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error, payload) =>
        error ? [] : ['Contracts', { type: 'Contracts', id: payload?.id }],
    }),

    downloadContractAttachmentsZip: builder.query<Blob, { contractId: number }>(
      {
        query: ({ contractId }) => ({
          url: `/contract/${contractId}/attachments/download-zip`,
          method: 'GET',
          responseHandler: (response: Response) => response.blob(),
        }),
      },
    ),

    // Contract Approval Mapping endpoints
    getContractApprovalMapping: builder.query<
      BackendResponse<any>,
      Partial<{
        page: number;
        pageSize: number;
        companyGroupId: number;
        companyId: number;
        contractTypeId: number;
        operationalUnitId: number;
        frequency: string;
        userId: number;
        regionId: number;
      }>
    >({
      query: params => ({
        url: `/contract-mappings/approval-config${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['ContractApprovalMapping'],
    }),

    getContractApprovalHierarchy: builder.query<
      Blob,
      Partial<{
        contractTypeId: number;
        userId: number;
        regionId: number;
      }>
    >({
      query: params => ({
        url: `/contract-mappings/approval-hierarchy${parseQueryParams(params)}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
      providesTags: ['ContractApprovalMapping'],
    }),

    createContractApprovalMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => {
        return {
          url: `/contract-mappings/approval-config`,
          method: 'POST',
          body: payload,
        };
      },
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['ContractApprovalMapping'];
      },
    }),

    updateContractApprovalMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => {
        const { id, ...rest } = payload;
        return {
          url: `/contract-mappings/approval-config/${id}`,
          method: 'PUT',
          body: rest,
        };
      },
      invalidatesTags: (_, error, { id }) => {
        if (error) {
          return [];
        }
        return [
          'ContractApprovalMapping',
          { type: 'ContractApprovalMapping', id: +id },
        ];
      },
    }),

    getContractApprovalMappingById: builder.query<BackendResponse<any>, number>(
      {
        query: id => ({
          url: `/contract-mappings/approval-config/${id}`,
          method: 'GET',
        }),
        providesTags: (__, err, id) =>
          err ? [] : [{ type: 'ContractApprovalMapping', id }],
      },
    ),

    deleteContractApprovalMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => {
        return {
          url: `/contract-mappings/approval-config${parseQueryParams(payload)}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['ContractApprovalMapping'];
      },
    }),

    downloadContractReport: builder.query<
      Blob,
      { action: ExportActions; query: any }
    >({
      query: ({ action, query }) => ({
        url: `/contract-reports/export/${action}${parseQueryParams(query)}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),
    createUniversalField: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/contract-field-mappings/universal-set`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['UniversalFieldSet']),
    }),

    getUniversalField: builder.query<
      BackendResponse<{ count: number; list: any[] }>,
      any
    >({
      query: query => {
        return {
          url: `/contract-field-mappings/universal-set${parseQueryParams(query)}`,
          method: 'GET',
        };
      },
    }),

    updateUniversalField: builder.mutation<
      BackendResponse<void>,
      { id: number; [key: string]: any }
    >({
      query: ({ id, ...rest }) => ({
        url: `/contract-field-mappings/universal-set/${id}`,
        method: 'PUT',
        body: rest,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['UniversalFieldSet']),
    }),

    deleteUniversalField: builder.mutation<
      BackendResponse<void>,
      number[] | string[] | number | string
    >({
      query: ids => ({
        url: `/contract-field-mappings/universal-set`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['UniversalFieldSet']),
    }),

    getFormFieldMappings: builder.query<
      any,
      Partial<{
        contractTypeId: string;
        page?: number;
        pageSize?: number;
        all?: boolean;
        search?: string;
      }>
    >({
      query: ({ contractTypeId, page, pageSize, all, search }) => ({
        url: '/contract-field-mappings/form-field-mappings',
        params: { contractTypeId, page, pageSize, all, search },
      }),
      providesTags: ['FormFieldMapping'],
    }),

    createFormFieldMapping: builder.mutation<any, any>({
      query: payload => ({
        url: '/contract-field-mappings/form-field-mappings',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['FormFieldMapping'],
    }),

    updateFormFieldMapping: builder.mutation<any, { id: number; body: any }>({
      query: ({ id, body }) => {
        return {
          url: `/contract-field-mappings/form-field-mappings/${id}`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: ['FormFieldMapping'],
    }),

    deleteFormFieldMapping: builder.mutation<any, { ids: string[] }>({
      query: configId => ({
        url: '/contract-field-mappings/form-field-mappings',
        method: 'DELETE',
        body: { configId },
      }),
      invalidatesTags: ['FormFieldMapping'],
    }),
    getFormFieldMappingById: builder.query<any, number>({
      query: id => ({
        url: `/contract-field-mappings/form-field-mappings/${id}`,
        method: 'GET',
      }),
    }),

    getFormFieldMappingsByContractType: builder.query<
      any,
      {
        contractTypeId: number;
        companyId?: number;
        companyGroupId?: number;
      }
    >({
      query: ({ contractTypeId, companyId, companyGroupId }) => {
        const params = new URLSearchParams();
        if (companyId) params.append('companyId', companyId.toString());
        if (companyGroupId)
          params.append('companyGroupId', companyGroupId.toString());
        const queryString = params.toString();
        return {
          url: `/contract-field-mappings/form-field-mappings-by-contract-type/${contractTypeId}${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['FormFieldMapping'],
    }),

    createSapConfigurationField: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/sap-configuration`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) =>
        error ? [] : ['SapConfigurationFieldSet'],
    }),

    getSapConfigurationField: builder.query<
      BackendResponse<{ count: number; list: any[] }>,
      any
    >({
      query: query => {
        return {
          url: `/sap-configuration${parseQueryParams(query)}`,
          method: 'GET',
        };
      },
    }),

    updateSapConfigurationField: builder.mutation<
      BackendResponse<void>,
      { id: number; [key: string]: any }
    >({
      query: ({ id, ...rest }) => ({
        url: `/sap-configuration/${id}`,
        method: 'PUT',
        body: rest,
      }),
      invalidatesTags: (_, error) =>
        error ? [] : ['SapConfigurationFieldSet'],
    }),

    deleteSapConfigurationField: builder.mutation<
      BackendResponse<void>,
      number[] | string[] | number | string
    >({
      query: ids => ({
        url: `/sap-configuration`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) =>
        error ? [] : ['SapConfigurationFieldSet'],
    }),
    setDefaultSapConfiguration: builder.mutation<
      BackendResponse<void>,
      number[] | string[] | number | string
    >({
      query: id => ({
        url: `/sap-configuration/${id}/set-default`,
        method: 'PATCH',
        body: { id },
      }),
      invalidatesTags: (_, error) =>
        error ? [] : ['SapConfigurationFieldSet'],
    }),

    approveContractFromEmail: builder.mutation<BackendResponse<any>, any>({
      query: payload => {
        return {
          url: `/contract-approvals/approve-from-mail`,
          method: 'POST',
          body: payload,
        };
      },
      invalidatesTags: (_, error, { id }) =>
        error
          ? []
          : [
              'ContractReports',
              { type: 'Contracts', id },
              { type: 'ContractApprovals' },
            ],
    }),

    // Contract Analytics APIs
    getContractSummary: builder.query<BackendResponse<ContractSummary>, void>({
      query: () => '/contract-analytics/summary',
    }),
    getContractApprovalFunnel: builder.query<
      BackendResponse<ApprovalFunnel>,
      void
    >({
      query: () => '/contract-analytics/approval-funnel',
    }),
    getContractPendingApprovalRequests: builder.query<
      BackendResponse<PendingApprovalRequests>,
      void
    >({
      query: () => '/contract-analytics/pending-approval-requests',
    }),
    getContractCreationTrends: builder.query<
      BackendResponse<CreationTrendsResponse>,
      CreationTrendsParams | void
    >({
      query: params => ({
        url: '/contract-analytics/contract-creation-trends',
        params: params || {},
      }),
    }),
    getContractApprovalTime: builder.query<
      BackendResponse<ApprovalTime>,
      ApprovalTimeParams | void
    >({
      query: params => ({
        url: '/contract-analytics/approval-time',
        params: params || {},
      }),
    }),
    getContractTopVendorsByCount: builder.query<
      BackendResponse<TopVendorsResponse>,
      TopVendorsParams | void
    >({
      query: params => ({
        url: '/contract-analytics/top-vendors-by-count',
        params: params || {},
      }),
    }),
    getContractSecurityAmountTracking: builder.query<
      BackendResponse<SecurityAmountTracking>,
      SecurityAmountParams | void
    >({
      query: params => ({
        url: '/contract-analytics/security-amount-tracking',
        params: params || {},
      }),
    }),
    getContractVendorPortfolio: builder.query<
      BackendResponse<ContractVendorPortfolio>,
      VendorPortfolioParams | void
    >({
      query: params => ({
        url: '/contract-analytics/vendor-portfolio',
        params: params || {},
      }),
    }),
    getContractExpiringContracts: builder.query<
      BackendResponse<ExpiringContractsResponse>,
      ExpiringContractsParams | void
    >({
      query: params => ({
        url: '/contract-analytics/expiring-contracts',
        params: params || {},
      }),
    }),
    getContractDurationDistribution: builder.query<
      BackendResponse<DurationDistribution>,
      DurationDistributionParams | void
    >({
      query: params => ({
        url: '/contract-analytics/contract-duration-distribution',
        params: params || {},
      }),
    }),

    resetApprovalHierarchy: builder.mutation<
      BackendResponse<void>,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/contract-approvals/reset-approval-hierarchy/${id}`,
        method: 'GET',
      }),
      invalidatesTags: (_, error) => (error ? [] : ['ResetApprovalHierarchy']),
    }),

    rearrangeApprovalHierarchy: builder.mutation<
      BackendResponse<any>,
      { id: string; mappings: any }
    >({
      query: ({ id, mappings }) => ({
        url: `/contract-approvals/rearrange-approval-hierarchy/${id}`,
        method: 'POST',
        body: mappings,
      }),
      invalidatesTags: (_, error) =>
        error ? [] : ['RearrangeApprovalHierarchy'],
    }),

    resendMailToCollaborators: builder.mutation<BackendResponse<any>, any>({
      query: payload => {
        return {
          url: `/contract/resend-collaborator-mails`,
          method: 'POST',
          body: payload,
        };
      },
      invalidatesTags: (_, error) => (error ? [] : ['Contracts']),
    }),
  }),
});

export const {
  useGetContractHomeDashboardQuery,
  useGetContractTypesDropdownQuery,
  useGetContractDocumentTemplatesQuery,
  useGetContractDocumentTemplateByIdQuery,
  useCreateContractDocumentTemplateMutation,
  useUpdateContractDocumentTemplateMutation,
  useDeleteContractDocumentTemplateMutation,
  useGetContractDocumentTemplateVariablesQuery,
  useGetInvoiceFrequencyOptionsQuery,
  useCreateContractDraftMutation,
  useCreateContractWithReferenceMutation,
  useCreateRenewalContractMutation,
  useGetContractRequestSubcontractsQuery,
  useUpdateContractDraftMutation,
  useGetContractsQuery,
  useGetContractDraftByIdQuery,
  useGetContractDocumentQuery,
  useUpdateContractDocumentMutation,
  useUpdateContractTokenDocumentMutation,
  useUploadContractAttachmentMutation,
  useGetContractAttachmentsQuery,
  useGetContractAttachmentFileQuery,
  useLazyGetContractAttachmentFileQuery,
  useDeleteContractAttachmentMutation,
  useGetContractApprovalHierarchyQuery,
  useSendContractForApprovalMutation,
  useGetContractConfigQuery,
  useGetContractAddressesQuery,
  useCreateContractAddressMutation,
  useUpdateContractAddressMutation,
  useGetUnitTypesQuery,
  useGetOperationalUnitsQuery,
  useGetInvitationTypesQuery,
  useGetBusinessPartnerTypesQuery,
  useGetBusinessCategoriesQuery,
  useLazyVendorCheckQuery,
  useSearchVendorsQuery,
  useLazySearchVendorsQuery,
  useAssociateVendorToContractMutation,
  useRemoveVendorFromContractMutation,
  useCreateVendorAndAssociateToContractMutation,
  useUpdateVendorThroughContractMutation,
  useResendVendorToVConnectMutation,
  useUpdateVendorSharePercentageMutation,
  useGetContractApprovalsQuery,
  useGetContractReportQuery,
  useLazyDownloadContractUploadSampleQuery,
  useUploadContractWorkbookMutation,
  useUpdateContractApprovalMutation,
  useBulkApproveContractsMutation,
  useApproveContractFromEmailMutation,
  useGetContractApprovalLogsByIdQuery,
  useGetContractChangeLogsByIdQuery,
  useDownloadClaimJSONByIdMutation,
  useLazyDownloadContractAttachmentJsonByIdQuery,
  useLazyPrintContractQuery,
  usePushContractToErpByIdMutation,
  useDeleteContractMutation,
  // useCreateContractMutation,
  // useGetContractByIdQuery,
  // useUpdateContractMutation,
  // useGetContractDocumentHistoryListQuery,
  // useLazyGetContractDocumentHistoryListQuery,
  // useLazyGetContractDocumentHistoryByIdQuery,
  // useRestoreDocumentHistoryMutation,
  // useUpdateSubmittedContractMutation,
  // useLazyGetTipTapExportTokenQuery,
  // useGetTipTapExportTokenQuery,
  useCreateSapConfigurationFieldMutation,
  useDeleteSapConfigurationFieldMutation,
  useGetSapConfigurationFieldQuery,
  useUpdateSapConfigurationFieldMutation,
  useSetDefaultSapConfigurationMutation,
  useLazyDownloadContractAttachmentsZipQuery,
  // Contract Approval Mapping
  useGetContractApprovalMappingQuery,
  useLazyGetContractApprovalHierarchyQuery,
  useCreateContractApprovalMappingMutation,
  useUpdateContractApprovalMappingMutation,
  useGetContractApprovalMappingByIdQuery,
  useDeleteContractApprovalMappingMutation,
  useLazyDownloadContractReportQuery,
  useGetFormFieldMappingByIdQuery,
  useGetFormFieldMappingsQuery,
  useCreateFormFieldMappingMutation,
  useUpdateFormFieldMappingMutation,
  useDeleteFormFieldMappingMutation,
  useCreateUniversalFieldMutation,
  useDeleteUniversalFieldMutation,
  useGetUniversalFieldQuery,
  useUpdateUniversalFieldMutation,
  useGetFormFieldMappingsByContractTypeQuery,
  useGetRecentContractDraftsQuery,
  // Contract Analytics
  useGetContractSummaryQuery,
  useGetContractApprovalFunnelQuery,
  useGetContractPendingApprovalRequestsQuery,
  useGetContractCreationTrendsQuery,
  useGetContractApprovalTimeQuery,
  useGetContractTopVendorsByCountQuery,
  useGetContractSecurityAmountTrackingQuery,
  useGetContractVendorPortfolioQuery,
  useGetContractExpiringContractsQuery,
  useGetContractDurationDistributionQuery,
  useResetApprovalHierarchyMutation,
  useRearrangeApprovalHierarchyMutation,
  useResendMailToCollaboratorsMutation,
} = contractDocumentDraftApi;

export default contractDocumentDraftApi;
